import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { makeStyles } from '@mui/styles';

import {
  Box,
  Switch,
  Typography,
} from '@mui/material';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import InheritanceField from '../InheritanceField';

import { NumberInput, AutocompleteCustom, AutocompleteWithChips } from '../../../components/Inputs';
import { checkValue, structureSelectOptions } from '../../../services/helpers/dataStructuring';
import parentPaths from '../../../services/paths';
import localization from '../../../localization';
import api from '../../../api';

import './FulfillmentAndSubscription.scss';

const useStyles = makeStyles({
  copyRelatedProductId: {
    color: '#4791db',
    '&:hover': {
      cursor: 'pointer',
      textDecoration: 'underline',
    },
  },
});

const Subscription = ({
  setProductData,
  currentProductData,
  selectOptions,
  parentId,
  relatedProduct,
  errors,
}) => {
  const history = useHistory();
  const classes = useStyles();

  const makeCopy = (value) => {
    navigator.clipboard.writeText(value)
      .then(() => toast(localization.t('general.itemHasBeenCopied')));
  };


  const subscriptionModels = selectOptions?.subscriptionModels?.map((item) => {
    return {
      id: item.id, 
      value: `${item?.value} (${item?.id})` 
    };
  });

  const { lifeTime, subscriptionTemplate } = currentProductData;

  return (
    <>
      <Box display="flex" flexDirection="row" alignItems="baseline">
        <Box p={2} width="50%">
          <InheritanceField
            field='subscriptionTemplate'
            onChange={setProductData}
            value={currentProductData?.subscriptionTemplate}
            parentId={parentId}
            currentProductData={currentProductData}
            buttonStyles={errors?.subscription?.includes('subscriptionModel') ? { bottom: '16px' } : {}}
          >
            <AutocompleteCustom
              optionLabelKey='value'
              label="subscriptionModel"
              onSelect={(newValue) => {
                setProductData({
                  ...currentProductData,
                  subscriptionTemplate: newValue,
                });
              }}
              error={errors?.subscription?.includes('subscriptionModel')}
              helperText={errors?.subscription?.includes('subscriptionModel') ? localization.t('labels.errorProductPerAndSub') : ''}
              selectOptions={subscriptionModels || []}
              curValue={checkValue(currentProductData?.subscriptionTemplate)}
            />
          </InheritanceField>
        </Box>
      </Box>

      <Box display="flex" flexDirection="row" alignItems="center" width={currentProductData?.trialAllowed ? "80%" : "50%"}>
        <Box p={2} width="50%" display="flex" flexDirection="row" alignItems="center">
          <Box p={2}>
            <Typography color="secondary">{localization.t('labels.allowTrial')}</Typography>
          </Box>

          <Box p={2}>
            <InheritanceField
              additionalField='trialDuration'
              additionalValue={currentProductData?.trialDuration}
              field='trialAllowed'
              onChange={setProductData}
              value={currentProductData?.trialAllowed}
              parentId={parentId}
              currentProductData={currentProductData}
            >
              <Switch
                disabled={!checkValue(currentProductData?.subscriptionTemplate)}
                name="allowTrial"
                onChange={(e) => {
                  setProductData({ ...currentProductData, trialAllowed: e.target.checked });
                }}
                color="primary"
                checked={checkValue(currentProductData?.trialAllowed) || false}
              />
            </InheritanceField>
          </Box>
        </Box>

        {currentProductData?.trialAllowed && (
          <Box p={2} width="50%" display="flex" flexDirection="row" alignItems="center">
            <Box p={2}>
              <Typography color="secondary">{localization.t('labels.allowPaidTrial')}</Typography>
            </Box>

            <Box p={2}>
              <Switch
                disabled={!checkValue(currentProductData?.subscriptionTemplate)}
                name="allowPaidTrial"
                onChange={(e) => {
                  setProductData({ ...currentProductData, paidTrial: e.target.checked });
                }}
                color="primary"
                checked={checkValue(currentProductData?.paidTrial) || false}
              />
            </Box>
          </Box>
        )}

        <Box p={2} width="50%">
          <InheritanceField
            field='trialDuration'
            onChange={setProductData}
            value={currentProductData?.trialDuration}
            parentId={parentId}
            currentProductData={currentProductData}
          >
            <NumberInput
              isDisabled={!checkValue(currentProductData.trialAllowed)}
              label="trialDuration"
              value={checkValue(currentProductData.trialDuration)}
              onChangeInput={(e) => {
                setProductData({ ...currentProductData, trialDuration: e.target.value });
              }}
              minMAx={{ min: 0 }}
            />
          </InheritanceField>
        </Box>
      </Box>

      {
        checkValue(lifeTime)?.name !== 'PERMANENT' && checkValue(subscriptionTemplate) && (
          <Box display="flex" flexDirection="row" alignItems="center">
            <Box p={2} width="50%">
              <AutocompleteWithChips
                curId={currentProductData?.id}
                getAdditionalOptions={(searchValue) => Promise.allSettled([
                  api.getRenewingProductsByCustomerId({ filters: `&id=${searchValue}`, customerId: currentProductData.customerId }),
                  api.getRenewingProductsByCustomerId({ filters: `&genericName=*${searchValue}*`, customerId: currentProductData.customerId })])
                  .then(([idSearch, nameSearch]) => {
                    const res = idSearch.value?.data?.items?.length
                      ? idSearch.value?.data.items : nameSearch.value?.data?.items;

                    return structureSelectOptions({ options: res, optionValue: 'genericName', adIddToValue: true });
                  })}
                data-test='renewingNameOrId'
                label='renewingNameOrId'
                arrayTypeValue
                arrayValue={checkValue(currentProductData?.nextGenerationOf)}
                selectOptions={selectOptions?.renewingProducts || []}
                onChange={(newValue) => setProductData(
                  { ...currentProductData, nextGenerationOf: newValue },
                )}
              />
            </Box>
          </Box>
        )
      }

      {relatedProduct && (
        <Box display="flex" flexDirection="row" alignItems="center">
          <Box p={2}>
            <Typography variant='h6' color="secondary">
              {localization.t('labels.relatedProduct')}
            </Typography>
          </Box>

          <Box pl={2}>
            <Typography variant='subtitle1' color="secondary">
              {`${relatedProduct.genericName},`}
            </Typography>
          </Box>

          <Box pl={1}>
            <Typography variant='subtitle1' className={classes.copyRelatedProductId}>
              <span
                onClick={() => history.push(`${parentPaths.productlist}/${relatedProduct.id}`)}
              >
                {relatedProduct.id}
              </span>
            </Typography>
          </Box>

          <Box p={1}>
            <FileCopyIcon
              onClick={() => makeCopy(relatedProduct.id)}
              color="secondary"
            />
          </Box>
        </Box>
      )}
    </>
  );
};

Subscription.propTypes = {
  setProductData: PropTypes.func,
  currentProductData: PropTypes.object,
  selectOptions: PropTypes.object,
  parentId: PropTypes.string,
  relatedProduct: PropTypes.object,
  errors: PropTypes.object,
};

export default Subscription;
